/* policyUtils.ts
   Utility functions for mutating a PolicyTemplate's conditions.
   Extracted from GraphView.tsx to decouple policy logic.
*/

import { produce } from 'immer';
import type { Draft } from 'immer';
import type {
  PolicyTemplate,
  Condition,
  NestedCondition,
  NotCondition,
  CountCondition,
  PolicyRuleIf,
} from '../../../../types/policy';
import {
  isCountCondition,
  isNestedCondition,
} from '../types/policy';

/**
 * Recursively removes a node and all its children from a nested policy structure.
 * @param currentLevel The current object or array being traversed.
 * @param pathSegments The remaining path segments to the target node.
 * @returns `true` if the node was found and removed, `false` otherwise.
 */
export function removeNodeAndChildren(
  currentLevel: any,
  pathSegments: (string | number)[]
): boolean {
  if (!currentLevel || pathSegments.length === 0) {
    console.warn("[removeNodeAndChildren] Invalid level or empty path.");
    return false; // <-- Return false on invalid input
  }
  const segment = pathSegments[0];
  const remainingPath = pathSegments.slice(1);
  if (remainingPath.length === 0) {
    if (Array.isArray(currentLevel) && typeof segment === 'number' && segment >= 0 && segment < currentLevel.length) {
        // Use console.debug for potentially verbose logs
        // console.debug(`Removing node at index ${segment} from array:`, JSON.parse(JSON.stringify(currentLevel)));
        currentLevel.splice(segment, 1);
        return true;
    } else if (typeof currentLevel === 'object' && currentLevel !== null && segment in currentLevel) {
        // console.debug(`Deleting property '${String(segment)}' from object:`, JSON.parse(JSON.stringify(currentLevel)));
        delete currentLevel[segment];
        return true;
    } else {
        console.warn(`[removeNodeAndChildren] Cannot remove. Invalid segment '${String(segment)}' for level:`, currentLevel);
        return false; // <-- Return false if segment invalid
    }
  }
  let nextLevel: any;
  if (Array.isArray(currentLevel) && typeof segment === 'number' && segment >= 0 && segment < currentLevel.length) {
    nextLevel = currentLevel[segment];
  } else if (typeof currentLevel === 'object' && currentLevel !== null && segment in currentLevel) {
    nextLevel = currentLevel[segment];
  } else {
    console.warn(`[removeNodeAndChildren] Cannot navigate. Invalid segment '${String(segment)}' for level:`, currentLevel);
    return false; // <-- Return false if cannot navigate
  }
  // Recursively call on the next level
  return removeNodeAndChildren(nextLevel, remainingPath);
}

/**
 * Deletes a node (and its subtree) at the given path from the policy rule's 'if' block.
 * After deletion, it recursively prunes any resulting empty parent objects (like {}, {allOf:[]}, etc.)
 * up the path hierarchy to maintain a clean policy structure.
 *
 * @param policy The original policy template.
 * @param pathToRemove The path (sequence of keys/indices) to the node to remove within the 'if' structure.
 * @returns A new PolicyTemplate object with the node removed and empty containers pruned.
 */
export function deleteNodeAndChildrenFromPolicy(
  policy: PolicyTemplate,
  pathToRemove: (string | number)[]
): PolicyTemplate {
    // Early exit if path is invalid or policy structure is missing
    if (!pathToRemove || pathToRemove.length === 0) {
        console.warn("[deleteNodeAndChildrenFromPolicy] Empty or invalid path provided.");
        return policy; // Return original policy if path is invalid
    }

    console.log(`Attempting delete at path: [${pathToRemove.join(' > ')}]`);

    return produce(policy, (draft: Draft<PolicyTemplate>) => {
        // Ensure the 'if' structure exists before attempting modification
        if (!draft.properties?.policyRule?.if) {
            console.warn("[deleteNode...Policy] No 'properties.policyRule.if' structure found in the draft policy.");
            return; // Exit Immer producer if 'if' doesn't exist
        }

        // --- 1. Remove the target node and its children ---
        // Use the recursive helper function to remove the node at the specified path within the 'if' block.
        const success = removeNodeAndChildren(draft.properties.policyRule.if, pathToRemove);

        // If the removal itself failed (e.g., invalid path partway through), log an error and potentially exit.
        if (!success) {
          console.error("[deleteNode...Policy] Failed to remove the target node using removeNodeAndChildren. Path might be invalid.", pathToRemove);
          // Depending on desired behavior, you might choose to stop here or proceed with pruning anyway.
          // For now, we'll proceed to prune, as partial path removal might still leave empty parents.
        }

        // --- 2. Recursively Prune Empty Parent Containers ---
        // Start pruning from the parent directory of the removed node.
        let prunePath = pathToRemove.slice(0, -1);

        // Loop upwards through the path segments as long as there's a path to check.
        while (prunePath.length > 0) {
          // --- Traverse to the container at the current prunePath ---
          let container: any = draft.properties!.policyRule!.if; // Start from the root 'if'
          let validPath = true; // Flag to track if traversal is successful

          // Iterate through the segments of the current prunePath to reach the container.
          for (const seg of prunePath) {
            // Check if the current container is valid and the next segment exists.
            if (container && typeof container === 'object' && seg in container) {
              container = container[seg]; // Move down one level
            } else {
              // If the path segment is invalid at any point, stop traversal for this prunePath.
              console.warn(`[deleteNode...Policy Pruning] Invalid segment '${String(seg)}' while traversing prunePath:`, prunePath);
              validPath = false;
              break; // Exit the inner for loop
            }
          }

          // --- Check if the container needs pruning ---
          // Proceed only if the traversal was successful.
          if (validPath) {
              // Check if the container is an empty object (e.g., {}, { not: {} }, { where: {} })
              // or an object containing only an empty array (e.g., { allOf: [] }, { anyOf: [] }).
              const isEmptyObject = container && typeof container === 'object' && !Array.isArray(container) && Object.keys(container).length === 0;
              const isEmptyAllOf = container?.allOf && Array.isArray(container.allOf) && container.allOf.length === 0;
              const isEmptyAnyOf = container?.anyOf && Array.isArray(container.anyOf) && container.anyOf.length === 0;

              // If the container is effectively empty...
              if (isEmptyObject || isEmptyAllOf || isEmptyAnyOf) {
                  console.log(`[deleteNode...Policy Pruning] Pruning empty container at path: [${prunePath.join(' > ')}]`);
                  // Use removeNodeAndChildren again to remove this now-empty container itself.
                  removeNodeAndChildren(draft.properties.policyRule.if, prunePath);
                  // Move up one level for the next iteration.
                  prunePath = prunePath.slice(0, -1);
              } else {
                  // If the container is not empty, stop pruning up this branch.
                  break; // Exit the while loop
              }
          } else {
              // If traversal failed, stop pruning up this branch.
              break; // Exit the while loop
          }
        } // End while loop

        // --- 3. Handle Root Case (Optional but good practice) ---
        // After pruning, check if the root 'if' itself became empty.
        const rootIf = draft.properties.policyRule.if;
        if (rootIf && typeof rootIf === 'object' && Object.keys(rootIf).length === 0) {
            console.log("[deleteNode...Policy] Root 'if' became empty after pruning. Resetting to an empty object (or null/undefined based on desired schema).");
            // Decide how to represent an empty root: {}, null, or remove 'if'?
            // Assigning {} is usually safest if 'if' is expected to exist.
            draft.properties.policyRule.if = {};
        }

        // No explicit return needed here, Immer handles returning the modified or original draft.
    });
}

/**
 * Adds a new condition or group at the specified path in the policy.
 */
export function addNodeToPolicy(
  policy: PolicyTemplate,
  targetPath: (string | number)[],
  newNode: Condition | NestedCondition | NotCondition | CountCondition
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    let grandparent: any = null;
    let parent: any = draft.properties?.policyRule;
    let current: any = draft.properties?.policyRule?.if;
    let parentSegment: string | number | null = 'if';
    for (const segment of targetPath) {
      // Initialize missing `where` on CountCondition
      if (
        segment === 'where' &&
        parent &&
        isCountCondition(parent) &&
        (current?.[segment] === undefined || current?.[segment] === null)
      ) {
        current[segment] = {};
      }
      grandparent = parent;
      parent = current;
      parentSegment = segment;
      current = current?.[segment];
      if (current === undefined) return;
    }
    // Handle empty `where` for CountCondition using the grandparent
    if (
      parentSegment === 'where' &&
      grandparent &&
      isCountCondition(grandparent) &&
      Object.keys(current || {}).length === 0
    ) {
      if (isNestedCondition(newNode)) {
        Object.assign(current, newNode);
      } else {
        Object.assign(current, newNode as Condition);
      }
      return;
    }
    // Determine array to push into
    let targetArray: any[] | null = null;
    if (Array.isArray(current?.allOf)) {
      targetArray = current.allOf;
    } else if (Array.isArray(current?.anyOf)) {
      targetArray = current.anyOf;
    }
    if (targetArray) {
      targetArray.push(newNode as Condition);
    }
  });
}

/**
 * Updates data of a condition node at the given path by shallow merging or replacing.
 */
export function updateNodeDataInPolicy(
  policy: PolicyTemplate,
  path: (string | number)[],
  newConditionData: Partial<Condition | NestedCondition | NotCondition | CountCondition>
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    let current: any = draft.properties?.policyRule?.if;
    let parent: any = draft.properties?.policyRule;
    let key: string | number = 'if';
    for (const segment of path) {
      parent = current;
      key = segment;
      current = current?.[segment];
    }
    const replaceKeys = ['field', 'value', 'count', 'not', 'allOf', 'anyOf'];
    const shouldReplace = replaceKeys.some((k) => k in newConditionData);
    if (shouldReplace) {
      parent[key] = newConditionData;
    } else if (current && typeof current === 'object') {
      Object.assign(current, newConditionData);
    }
  });
}

/**
 * Moves a node from one index to another within its sibling array.
 */
export function moveNodeInPolicy(
  policy: PolicyTemplate,
  path: (string | number)[],
  newIndex: number
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    let arr: any = draft.properties?.policyRule?.if;
    for (let i = 0; i < path.length - 1; i++) {
      arr = arr?.[path[i]];
    }
    const idx = path[path.length - 1] as number;
    const [item] = arr?.splice(idx, 1) || [];
    if (item) {
      arr?.splice(newIndex, 0, item);
    }
  });
}

/**
 * Toggles between 'allOf' and 'anyOf' for a group node at the specified path.
 */
export function toggleGroupTypeInPolicy(
  policy: PolicyTemplate,
  path: (string | number)[]
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    let current: any = draft.properties?.policyRule?.if;
    for (const segment of path) {
      current = current?.[segment];
    }
    if (Array.isArray(current?.allOf)) {
      current.anyOf = current.allOf;
      delete current.allOf;
    } else if (Array.isArray(current?.anyOf)) {
      current.allOf = current.anyOf;
      delete current.anyOf;
    }
  });
}

/**
 * Wraps or unwraps a node in a 'not' at the given path.
 */
export function toggleNotInPolicy(
  policy: PolicyTemplate,
  path: (string | number)[]
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    let parent: any = draft.properties?.policyRule;
    let current: any = draft.properties?.policyRule?.if;
    let key: string | number = 'if';
    for (const segment of path) {
      parent = current;
      key = segment;
      current = current?.[segment];
    }
    if (current?.not) {
      parent[key] = current.not;
    } else {
      parent[key] = { not: current };
    }
  });
}

/**
 * Sets or resets the root node of the policy.
 */
export function setRootNodeInPolicy(
  policy: PolicyTemplate,
  newRoot: Condition
): PolicyTemplate {
  return produce(policy, (draft: Draft<PolicyTemplate>) => {
    if (!draft.properties) {
      draft.properties = {} as Draft<PolicyTemplate['properties']>;
    }
    if (!draft.properties.policyRule) {
      draft.properties.policyRule = {} as Draft<PolicyTemplate['properties']['policyRule']>;
    }
    // Use proper typing for the if clause
    draft.properties.policyRule.if = newRoot as Draft<PolicyRuleIf>;
  });
} 